'use client';

import { Input, Flex } from '@/_components';
import { useContext, useEffect, useState, type FC } from 'react';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import type { QuestionFormDateProps } from './QuestionFormDate.types';
import { useDebounce } from '@/_hooks/useDebounce';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { usePrevious } from '@/_hooks/usePrevious';
import { SAVE_TEXT_DELAY_MS } from '@/_constants/questions';

export const QuestionFormDate: FC<QuestionFormDateProps> = ({
  question,
  mode,
  isDisabled,
}) => {
  const { updateQuestion } = useContext(ClientQuestionnaireContext);
  const { updatePreviewQuestion } = useContext(PreviewContext);

  const previousQuestionId: number = usePrevious(question.id);

  const [text, setText] = useState<string>(question?.answer || '');
  const [isMounted, setMounted] = useState<boolean>(false);
  const [isBlurred, setBlurred] = useState<boolean>(false);

  const debouncedText: string = useDebounce<string>(text, SAVE_TEXT_DELAY_MS);

  const onTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setText(event.target.value);
  };

  const onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setBlurred(true);
    onTextChange(event);
  };

  useEffect(() => {
    if (question?.id !== previousQuestionId) {
      setMounted(false);
      setText(question?.answer || '');
    }

    setTimeout(() => setMounted(true), 300);

    return () => setMounted(false);
  }, [question?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const _onSave = () => {
      const answerParams = { questionId: question.id, answer: text };

      if (mode === 'admin-preview') return updatePreviewQuestion(answerParams);
      if (mode === 'admin-template') return updatePreviewQuestion(answerParams);

      updateQuestion(answerParams);
    };

    const shouldSave = !!text && isMounted && text !== question.answer;

    shouldSave && _onSave();
  }, [debouncedText, isBlurred]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex gap={4}>
      <Input
        label="Answer"
        isLabelHidden={true}
        autoComplete="new-password"
        isDisabled={isDisabled}
        placeholder="Your Answer"
        type="date"
        onBlur={onBlur}
        onChange={onTextChange}
        onFocus={() => setBlurred(false)}
        value={text}
      />
    </Flex>
  );
};
